import {
  ALLOWED_FILE_EXTENSIONS,
  LOGO_ALLOWED_INPUT_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_LOGO_SIZE_BYTES,
  MAX_LOGO_SIZE_MB,
  MAX_LOGO_STORED_DIMENSION_PX,
  MIN_LOGO_DIMENSION_PX,
} from "../constants";
import { apiService } from "../services/apiService";

import type { FileError } from "react-dropzone";

const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf(".");
  return lastDotIndex === -1
    ? ""
    : filename.slice(lastDotIndex + 1).toLowerCase();
};

const isFileExtensionAllowed = (filename: string): boolean => {
  const extension = getFileExtension(filename);
  return ALLOWED_FILE_EXTENSIONS.includes(extension);
};

const isFileSizeAllowed = (file: File): boolean =>
  file.size <= MAX_FILE_SIZE_BYTES;

export const validateFile = (file: File): FileError | null => {
  if (!isFileExtensionAllowed(file.name)) {
    return {
      code: "invalid_file_type",
      message: "File type not allowed",
    };
  }

  if (!isFileSizeAllowed(file)) {
    return {
      code: "invalid_file_size",
      message: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    };
  }

  return null;
};

export const bytesToMb = (bytes: number): string =>
  (bytes / (1024 * 1024)).toFixed(2);

export interface FileUploadResult {
  success: boolean;
  fileName: string;
  error?: string;
}

export const uploadFileToS3 = async (
  file: File,
  presignedUrl: string,
  userId: string,
): Promise<FileUploadResult> => {
  try {
    const response = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "x-amz-meta-original_filename": file.name,
        "x-amz-meta-uploaded_by_user_id": userId,
      },
      body: file,
    });

    if (response.ok) {
      return {
        success: true,
        fileName: file.name,
      };
    }

    return {
      success: false,
      fileName: file.name,
      error: `Upload failed: ${response.status} ${response.statusText}`,
    };
  } catch (error) {
    return {
      success: false,
      fileName: file.name,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

const getPresignedUrl = async (
  url: string,
  fileName: string,
  contentType: string,
): Promise<string> => {
  const response = await apiService.request<PreSignedURLResponse>("post", url, {
    original_filename: fileName,
    content_type: contentType,
  });
  return response.data.url;
};

export const uploadFilesToS3 = async (
  files: File[],
  presignedUrlEndpoint: string,
  userId: string,
): Promise<FileUploadResult[]> => {
  const uploadPromises = files.map(async (file) => {
    try {
      const presignedUrl = await getPresignedUrl(
        presignedUrlEndpoint,
        file.name,
        file.type,
      );
      return await uploadFileToS3(file, presignedUrl, userId);
    } catch (error) {
      return {
        success: false,
        fileName: file.name,
        error:
          error instanceof Error ? error.message : "Failed to get upload URL",
      };
    }
  });

  return Promise.all(uploadPromises);
};

const LOGO_PNG_CONTENT_TYPE = "image/png";

function toPngFilename(originalName: string): string {
  const withoutExtension = originalName.replace(/\.[^.]+$/, "");
  const safeStem = withoutExtension.length > 0 ? withoutExtension : "logo";
  return `${safeStem}.png`;
}

async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("That file could not be read as an image."));
    };
    image.src = objectUrl;
  });
}

function scaleToMaxEdge(width: number, height: number) {
  const longestEdge = Math.max(width, height);
  if (longestEdge <= MAX_LOGO_STORED_DIMENSION_PX) return { width, height };
  const ratio = MAX_LOGO_STORED_DIMENSION_PX / longestEdge;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

/**
 * Validates a picked logo and re-encodes it to PNG, downscaled so the longest
 * edge is at most MAX_LOGO_STORED_DIMENSION_PX.
 *
 * The backend stores PNG only and has no image library, so format and dimension
 * rules are enforced here (see docs/engineering/design/carrier_company_logo_phase_2_design.md
 * §4). Rejects rather than resolving to a partial result, so a caller never ends
 * up uploading something unvalidated.
 */
export const normalizeLogoToPng = async (file: File): Promise<File> => {
  if (!LOGO_ALLOWED_INPUT_TYPES.includes(file.type as never)) {
    throw new Error("Choose a PNG, JPEG or WEBP image.");
  }

  if (file.size > MAX_LOGO_SIZE_BYTES) {
    throw new Error(
      `That image is ${bytesToMb(file.size)}MB. Choose one under ${MAX_LOGO_SIZE_MB}MB.`,
    );
  }

  const image = await loadImageFromFile(file);

  if (
    image.naturalWidth < MIN_LOGO_DIMENSION_PX ||
    image.naturalHeight < MIN_LOGO_DIMENSION_PX
  ) {
    throw new Error(
      `That image is ${image.naturalWidth}x${image.naturalHeight}. Use one at least ${MIN_LOGO_DIMENSION_PX}x${MIN_LOGO_DIMENSION_PX}.`,
    );
  }

  const { width, height } = scaleToMaxEdge(
    image.naturalWidth,
    image.naturalHeight,
  );
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("That image could not be processed. Please try again.");
  }
  context.drawImage(image, 0, 0, width, height);

  const pngBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, LOGO_PNG_CONTENT_TYPE);
  });
  if (pngBlob === null) {
    throw new Error("That image could not be processed. Please try again.");
  }

  // uploadFileToS3 reads .name and .type off a File, so wrap the blob rather
  // than passing it straight through.
  return new File([pngBlob], toPngFilename(file.name), {
    type: LOGO_PNG_CONTENT_TYPE,
  });
};
